import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  HomeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { isTokenValid, clearAdminAuth } from '../utils/auth';

interface FeaturedVehicle {
  id: number;
  make: string;
  model: string;
  year: number;
  price: number;
  is_available: boolean;
  is_sold: boolean;
  created_at: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<FeaturedVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scraperStatus, setScraperStatus] = useState<any>(null);
  const [usaScraperStatus, setUsaScraperStatus] = useState<any>(null);
  const [scrapingNow, setScrapingNow] = useState(false);
  const [usaScrapingNow, setUsaScrapingNow] = useState(false);
  const username = localStorage.getItem('admin_username') || 'Admin';

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('admin_token');
    if (!token || !isTokenValid(token)) {
      clearAdminAuth();
      navigate('/admin/login', { replace: true });
      return;
    }

    fetchVehicles();
    fetchScraperStatus();
    fetchUsaScraperStatus();
  }, [navigate]);

  const fetchScraperStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }

      const response = await fetch('/api/v1/admin/scraper/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setScraperStatus(data);
      } else if (response.status === 401) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
      }
    } catch (err) {
      console.error('Failed to fetch scraper status:', err);
    }
  };

  const fetchUsaScraperStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        return;
      }

      const response = await fetch('/api/v1/admin/scraper/usa-status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsaScraperStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch USA scraper status:', err);
    }
  };

  const runScraperNow = async () => {
    if (!window.confirm('Start IAAI Canada scraper now?')) {
      return;
    }

    setScrapingNow(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }
      const response = await fetch('/api/v1/admin/scraper/run', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ IAAI Canada scraper started!');
        setTimeout(fetchScraperStatus, 5000);
      } else if (response.status === 401) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      } else {
        alert('❌ Failed to start scraper');
      }
    } catch (err) {
      alert('❌ Error starting scraper');
    } finally {
      setScrapingNow(false);
    }
  };

  const runUsaScraperNow = async () => {
    if (!window.confirm('Start USA scraper (bid.cars)? This will scrape premium brands from IAAI USA and Copart.')) {
      return;
    }

    setUsaScrapingNow(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }
      const response = await fetch('/api/v1/admin/scraper/usa-run', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('✅ USA scraper started! This may take a few minutes.');
        setTimeout(fetchUsaScraperStatus, 5000);
      } else if (response.status === 401) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      } else {
        alert('❌ Failed to start USA scraper');
      }
    } catch (err) {
      alert('❌ Error starting USA scraper');
    } finally {
      setUsaScrapingNow(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token || !isTokenValid(token)) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }
      const response = await fetch('/api/v1/featured-inventory/', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        clearAdminAuth();
        navigate('/admin/login', { replace: true });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch vehicles');
      }

      const data = await response.json();
      setVehicles(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/v1/featured-inventory/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setVehicles(vehicles.filter(v => v.id !== id));
      } else {
        alert('Failed to delete vehicle');
      }
    } catch (err) {
      alert('Error deleting vehicle');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                {vehicles.length} Vehicles
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <UserCircleIcon className="w-5 h-5" />
                <span>{username}</span>
              </div>
              <button
                onClick={() => navigate('/admin/social-media')}
                className="inline-flex items-center px-4 py-2 border border-purple-600 text-purple-600 rounded-lg text-sm font-medium bg-white hover:bg-purple-50 transition-colors"
              >
                <SparklesIcon className="w-4 h-4 mr-2" />
                Social Media
              </button>
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                View Website
              </a>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Scraper Status - Canada */}
        {scraperStatus && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🇨🇦 IAAI Canada Scraper</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Last Sync:</span>
                    <p className="font-medium text-gray-900">
                      {scraperStatus.last_sync 
                        ? new Date(scraperStatus.last_sync).toLocaleString('bg-BG')
                        : 'Never'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Vehicles:</span>
                    <p className="font-medium text-gray-900">{scraperStatus.total_vehicles}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Active:</span>
                    <p className="font-medium text-green-600">{scraperStatus.active_vehicles}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={runScraperNow}
                disabled={scrapingNow}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {scrapingNow ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Running...
                  </>
                ) : (
                  '▶️ Run Scraper Now'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Scraper Status - USA */}
        {usaScraperStatus && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">🇺🇸 USA Scraper (IAAI + Copart)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Last Sync:</span>
                    <p className="font-medium text-gray-900">
                      {usaScraperStatus.last_sync 
                        ? new Date(usaScraperStatus.last_sync).toLocaleString('bg-BG')
                        : 'Never'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Total USA:</span>
                    <p className="font-medium text-gray-900">{usaScraperStatus.total_usa_vehicles}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">IAAI USA:</span>
                    <p className="font-medium text-purple-600">{usaScraperStatus.iaai_usa_vehicles}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Copart:</span>
                    <p className="font-medium text-orange-600">{usaScraperStatus.copart_vehicles}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={runUsaScraperNow}
                disabled={usaScrapingNow}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {usaScrapingNow ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Running...
                  </>
                ) : (
                  '▶️ Run USA Scraper'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Featured Inventory</h2>
            <p className="text-sm text-gray-600 mt-1">Manage your clean vehicles for sale</p>
          </div>
          <button
            onClick={() => navigate('/admin/featured-inventory/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New Vehicle
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-gray-600">Loading vehicles...</p>
          </div>
        ) : vehicles.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <PlusIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles yet</h3>
            <p className="text-gray-600 mb-6">Get started by adding your first featured vehicle</p>
            <button
              onClick={() => navigate('/admin/featured-inventory/new')}
              className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Your First Vehicle
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {vehicles.map((vehicle) => (
                    <tr key={vehicle.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-semibold">
                          {Math.round(vehicle.price).toLocaleString()} €
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {vehicle.is_sold ? (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                            Sold
                          </span>
                        ) : vehicle.is_available ? (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Available
                          </span>
                        ) : (
                          <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Unavailable
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(vehicle.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => navigate(`/admin/featured-inventory/${vehicle.id}`)}
                          className="text-primary-600 hover:text-primary-900 mr-4 inline-flex items-center"
                        >
                          <PencilIcon className="w-4 h-4 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(vehicle.id)}
                          className="text-red-600 hover:text-red-900 inline-flex items-center"
                        >
                          <TrashIcon className="w-4 h-4 mr-1" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {vehicles.map((vehicle) => (
                <div key={vehicle.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h3>
                      <p className="text-xl font-bold text-primary-600 mt-1">
                        {Math.round(vehicle.price).toLocaleString()} €
                      </p>
                    </div>
                    {vehicle.is_sold ? (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                        Sold
                      </span>
                    ) : vehicle.is_available ? (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        Available
                      </span>
                    ) : (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Unavailable
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-500 mb-4">
                    Added: {new Date(vehicle.created_at).toLocaleDateString()}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => navigate(`/admin/featured-inventory/${vehicle.id}`)}
                      className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle.id)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;

